import { useEffect, useState } from "react";
import { Loader2, Crown, GitCompareArrows } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ConfidenceRing } from "./ConfidenceRing";
import { api } from "../lib/api";

export const HeadToHeadModal = ({ propA, propB, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && propA && propB) {
      setLoading(true);
      setData(null);
      setError(null);
      api
        .post("/ai/head-to-head", { prop_id_a: propA.id, prop_id_b: propB.id })
        .then((r) => setData(r.data))
        .catch((e) => setError(e.response?.data?.detail || "AI comparison failed."))
        .finally(() => setLoading(false));
    }
  }, [open, propA, propB]);

  const pickIsA = data && propA && data.pick_player === propA.player_name;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="h2h-modal"
        className="bg-[#141C2B] border-[#233248] text-white max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <GitCompareArrows className="h-5 w-5 text-[#00E5FF]" /> Head-to-Head
          </DialogTitle>
        </DialogHeader>

        {propA && propB && (
          <div className="grid grid-cols-2 gap-3 mb-2">
            {[propA, propB].map((p, i) => (
              <div key={i} className="rounded-xl border border-[#233248] bg-[#0B0F17]/60 p-3 text-center">
                <div className="text-white font-semibold text-sm">{p.player_name}</div>
                <div className="font-data text-[11px] text-slate-500">{p.team_abbr} · {p.position}</div>
                <div className="font-data text-xs text-[#00E5FF] mt-1">{p.line} {p.metric}</div>
              </div>
            ))}
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center gap-2 py-8 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin text-[#00E5FF]" />
            <span className="text-sm">AI comparing matchups…</span>
          </div>
        )}

        {error && <p className="text-sm text-[#EF4444] text-center py-4">{error}</p>}

        {data && !loading && (
          <div className="space-y-3">
            <div className="rounded-xl border border-[#00E5FF]/40 bg-[#00E5FF]/5 p-3 flex items-center gap-3">
              <ConfidenceRing value={data.confidence} />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 text-[#00E5FF] text-xs font-semibold">
                  <Crown className="h-3.5 w-3.5" /> AI EDGE PICK
                </div>
                <div className="text-white font-bold">{data.pick_player}</div>
                <div className="font-data text-xs text-slate-300">
                  {data.pick_lean} {data.pick_metric} · {data.edge}
                </div>
              </div>
            </div>

            {data.comparison?.length > 0 && (
              <div className="rounded-xl border border-[#233248] overflow-hidden">
                <div className="grid grid-cols-3 bg-[#0B0F17] text-[11px] text-slate-500 font-data px-3 py-1.5">
                  <span className={pickIsA ? "text-[#00E5FF]" : ""}>{propA?.player_name.split(" ").pop()}</span>
                  <span className="text-center">Metric</span>
                  <span className={`text-right ${!pickIsA ? "text-[#00E5FF]" : ""}`}>
                    {propB?.player_name.split(" ").pop()}
                  </span>
                </div>
                {data.comparison.map((row, i) => (
                  <div key={i} className="grid grid-cols-3 items-center px-3 py-2 text-sm border-t border-[#233248]">
                    <span className="font-data text-white">{row.a}</span>
                    <span className="text-center text-[11px] text-slate-400">{row.label}</span>
                    <span className="font-data text-white text-right">{row.b}</span>
                  </div>
                ))}
              </div>
            )}

            {data.reasoning && <p className="text-sm text-slate-300 leading-relaxed">{data.reasoning}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
